import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Add } from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import PageBand from "../../../layouts/page-detail/PageBand";
import SearchBox from "../../../components/form/SearchBox/SearchBox";
import {
  ModuleNames,
  SEARCHBOX,
  SEVERITIES,
} from "../../../data/constants";
import { useMenu } from "../../../context/MenuContext";
import WorkflowDefinitionCard, {
  WorkflowCardAction,
} from "../../../features/workflows/WorkflowDefinitionCard";
import {
  buildCopyName,
  getLibraryInstanceTotal,
  toWorkflowLibraryItem,
  WorkflowLibraryItem,
} from "../../../features/workflows/workflowLibraryModel";
import useApi from "../../../hooks/useApi";
import { useToaster } from "../../../hooks/useToaster";
import {
  duplicateWorkflowDefinition,
  getWorkflowDefinitions,
} from "../../../services/workflowDefinitionService";
import "./WorkflowLibrary.scss";

type StatusFilter = "active" | "draft" | "published" | "archived" | "all";
type LoadState = "loading" | "loaded" | "failed";

const ALL_CATEGORIES = "all";

const pluralize = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

// Same value PifBreadcrumbs uses for its on-banner links — plain inherited text is unreadable
// against the brown band.
const BREADCRUMB_LINK_COLOR = "rgba(255, 255, 255, 0.75)";

const WorkflowLibrary: React.FC = () => {
  const navigate = useNavigate();
  const showToaster = useToaster();
  const { executeApi } = useApi();
  const { permissions } = useMenu();
  const [definitions, setDefinitions] = useState<WorkflowLibraryItem[]>([]);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  // Bumped to re-read the list: Retry after a failed load, and after a refused duplicate.
  const [reloadKey, setReloadKey] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [status, setStatus] = useState<StatusFilter>("active");
  const [category, setCategory] = useState(ALL_CATEGORIES);
  const [announcement, setAnnouncement] = useState("");
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadState("loading");

    // useApi has already shown the alert when this resolves null.
    executeApi(getWorkflowDefinitions, undefined).then((items) => {
      if (cancelled) return;
      if (items === null) {
        setLoadState("failed");
        return;
      }
      setDefinitions(items.map(toWorkflowLibraryItem));
      setLoadState("loaded");
    });

    return () => {
      cancelled = true;
    };
  }, [executeApi, reloadKey]);

  // Epic 80427 grants.
  const hasGrant = (moduleName: string, right: "canView" | "canEdit") =>
    permissions.some((permission) => permission.moduleName === moduleName && Boolean(permission[right]));
  // Duplicate and New create a draft, which is Builder CanEdit — the same check MenuGuard makes.
  const canEditWorkflows = hasGrant(ModuleNames.WorkflowBuilder, "canEdit");
  // Open lands on the Builder, which MenuGuard admits on Builder CanView (an editor has it too).
  const canOpenWorkflows = canEditWorkflows || hasGrant(ModuleNames.WorkflowBuilder, "canView");
  // Starting a case outside the Builder is My Work CanEdit (Epic 80430).
  const canStartCases = hasGrant(ModuleNames.WorkflowMyWork, "canEdit");

  const categories = useMemo(
    () =>
      Array.from(new Set(definitions.map((definition) => definition.category))).sort(
        (left, right) => left.localeCompare(right),
      ),
    [definitions],
  );

  const visibleDefinitions = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    return definitions.filter((definition) => {
      const matchesStatus =
        status === "all" ||
        (status === "active" && definition.status !== "archived") ||
        definition.status === status;
      const matchesCategory =
        category === ALL_CATEGORIES || definition.category === category;
      const matchesSearch =
        !query ||
        definition.name.toLowerCase().includes(query) ||
        definition.category.toLowerCase().includes(query);

      return matchesStatus && matchesCategory && matchesSearch;
    });
  }, [category, definitions, searchText, status]);

  // Library-wide on purpose: it ignores the search and filters.
  const instanceTotal = useMemo(() => getLibraryInstanceTotal(definitions), [definitions]);

  const handleStatusChange = (event: SelectChangeEvent<StatusFilter>) => {
    setStatus(event.target.value as StatusFilter);
  };

  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    setCategory(event.target.value);
  };

  const handleDuplicate = async (definition: WorkflowLibraryItem) => {
    if (duplicatingId !== null) return;
    setDuplicatingId(definition.id);

    // No newToken: a copy must never carry the source's Token (Tokens are unique, and the PIF is
    // recognised by its Token). The API doesn't generate one either, so the copy has no Token.
    const newName = buildCopyName(definition.name, definitions);
    const outcome = await executeApi(duplicateWorkflowDefinition, {
      sourceDefinitionId: definition.id,
      mode: "newWorkflow",
      newName,
    });
    setDuplicatingId(null);

    // null = useApi already alerted (404, 500, network).
    if (!outcome) return;

    if (outcome.kind === "ok") {
      showToaster(`"${newName}" was created as a draft.`, SEVERITIES.SUCCESS);
      navigate(`/workflow/builder?definitionId=${outcome.data.newDefinitionId}`);
      return;
    }

    // Refused (e.g. the name was taken in a race). Re-read the list so the next attempt numbers
    // the copy past whatever now exists, and leave the button usable for that retry.
    const reason = outcome.kind === "invalid" ? outcome.issues[0]?.message : undefined;
    const message = `"${definition.name}" couldn't be duplicated${reason ? `: ${reason}` : "."} Please try again.`;
    showToaster(message, SEVERITIES.ERROR);
    setAnnouncement(message);
    setReloadKey((key) => key + 1);
  };

  const handleCardAction = (action: WorkflowCardAction, definition: WorkflowLibraryItem) => {
    if (action === "Open") {
      navigate(`/workflow/builder?definitionId=${definition.id}`);
      return;
    }
    if (action === "Run") {
      // By Token when there is one: the Run screen resolves it to the family's latest PUBLISHED
      // version. Most workflows have no Token (the Builder saves a new workflow, and Duplicate
      // creates a copy, without one), so otherwise start the listed version - Run is only offered
      // on a published card, so that is the published version.
      navigate(
        definition.token
          ? `/workflow/run?token=${encodeURIComponent(definition.token)}`
          : `/workflow/run?definitionId=${definition.id}`,
      );
      return;
    }
    if (action === "Duplicate") {
      handleDuplicate(definition);
    }
  };

  const handleNewWorkflow = () => {
    // No id: the Builder starts a blank draft, and the first save creates it.
    navigate("/workflow/builder");
  };

  const renderBody = () => {
    if (loadState === "loading") {
      return (
        <Box className="workflow-library__state" role="status" aria-live="polite">
          <CircularProgress size={28} />
          <Typography>Loading workflows…</Typography>
        </Box>
      );
    }

    if (loadState === "failed") {
      return (
        <Box className="workflow-library__empty" role="alert">
          <Typography component="h2">{"The workflow library couldn't be loaded"}</Typography>
          <Typography>Check your connection and try again.</Typography>
          <Button
            className="workflow-library__retry"
            variant="outlined"
            onClick={() => setReloadKey((key) => key + 1)}
          >
            Retry
          </Button>
        </Box>
      );
    }

    if (definitions.length === 0) {
      return (
        <Box className="workflow-library__empty">
          <Typography component="h2">No workflows yet</Typography>
          <Typography>
            {canEditWorkflows
              ? "Use New workflow to build the first one."
              : "Workflows will appear here once they are created."}
          </Typography>
        </Box>
      );
    }

    if (visibleDefinitions.length === 0) {
      return (
        <Box className="workflow-library__empty">
          <Typography component="h2">No workflow definitions found</Typography>
          <Typography>Try changing the search or filters.</Typography>
        </Box>
      );
    }

    return (
      <Box className="workflow-library__grid">
        {visibleDefinitions.map((definition) => (
          <WorkflowDefinitionCard
            key={definition.id}
            definition={definition}
            onAction={handleCardAction}
            canRun={canStartCases && definition.status === "published"}
            canDuplicate={canEditWorkflows}
            canOpen={canOpenWorkflows}
            isDuplicating={definition.id === duplicatingId}
          />
        ))}
      </Box>
    );
  };

  return (
    <main className="workflow-library">
      {/* App.tsx skips its own PageDetail for /workflow, so the page supplies the title and trail. */}
      <PageBand
        title="Workflow Library"
        breadcrumbs={
          <Breadcrumbs
            aria-label="breadcrumbs"
            sx={{ "& .MuiBreadcrumbs-separator": { color: BREADCRUMB_LINK_COLOR } }}
          >
            <Typography sx={{ color: BREADCRUMB_LINK_COLOR }}>Workflow Management</Typography>
            <Typography sx={{ fontWeight: 600, color: "#fff" }}>Library</Typography>
          </Breadcrumbs>
        }
      />

      <section className="workflow-library__content" aria-label="Workflow definitions">
        <Box className="workflow-library__toolbar">
          <Box className="search-box-wrap workflow-library__search">
            <SearchBox searchPlaceholder={SEARCHBOX.TEXT_PLACEHOLDER} onSearch={setSearchText} />
          </Box>

          <FormControl className="workflow-library__filter" size="small">
            <InputLabel id="workflow-status-label">Status</InputLabel>
            <Select
              labelId="workflow-status-label"
              label="Status"
              value={status}
              onChange={handleStatusChange}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>

          <FormControl className="workflow-library__filter" size="small">
            <InputLabel id="workflow-category-label">Category</InputLabel>
            <Select
              labelId="workflow-category-label"
              label="Category"
              value={category}
              onChange={handleCategoryChange}
            >
              <MenuItem value={ALL_CATEGORIES}>All categories</MenuItem>
              {categories.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {canEditWorkflows && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleNewWorkflow}
              className="workflow-library__new-button"
            >
              New workflow
            </Button>
          )}
        </Box>

        {loadState === "loaded" && (
          <Typography className="workflow-library__count" aria-live="polite">
            {pluralize(visibleDefinitions.length, "definition")}
            {/* Unknown until the API serves real counts — definitions only, never a fake 0. */}
            {instanceTotal !== null && ` · ${pluralize(instanceTotal, "instance")}`}
          </Typography>
        )}

        {renderBody()}

        <Typography className="workflow-library__announcement" role="status" aria-live="polite">
          {announcement}
        </Typography>
      </section>
    </main>
  );
};

export default WorkflowLibrary;
